/**
 * Pure resolver for the `?lang=XX` URL parameter (#2235).
 *
 * Given a language code and the session's translation list, returns:
 * - `{ translationId: string }` when the code matches a translation,
 * - `{ translationId: null, stale: true }` when the code was supplied
 *   but no translation matches (deleted link, bad share, typo),
 * - `{ translationId: null, stale: false }` when the code is absent
 *   (user is viewing the original — single-column layout).
 *
 * Lives next to the editor so the transcript-main.tsx hook can call
 * it and the unit test can call it on its own without mounting
 * React.
 */

export interface TranslationDoc {
  id: string;
  language?: string | null;
}

export interface ResolvedTranslation {
  translationId: string | null;
  /** True when a `?lang=` was supplied but no translation matched. */
  stale: boolean;
}

export function resolveTranslationFromLang(
  lang: string | null | undefined,
  translations: readonly TranslationDoc[]
): ResolvedTranslation {
  const code = (lang ?? '').trim().toUpperCase();
  if (!code) return { translationId: null, stale: false };
  const match = translations.find(d => d.language?.toUpperCase() === code);
  if (!match) return { translationId: null, stale: true };
  return { translationId: match.id, stale: false };
}

/**
 * Derive the split-view layout decision. Pure, matches the rendering
 * guard in `transcript-main.tsx`:
 * - `translationId` present AND at least one translation exists →
 *   split layout.
 * - Otherwise → single-column.
 */
export function layoutFromLang(
  lang: string | null | undefined,
  translations: readonly TranslationDoc[]
): 'split' | 'single' {
  const { translationId } = resolveTranslationFromLang(lang, translations);
  return translationId && translations.length > 0 ? 'split' : 'single';
}
